<template>
    <div class="flex gap-6 lg:gap-16">
        <div>
            <label
                for="contractStartDate"
                class="block font-semibold"
                >{{ t('startDate') }}</label
            >
            <div v-if="startDateOptions.length === 1">
                {{ formattedStartDate }}
            </div>
            <dropdown
                v-else
                :model-value="selectedStartDate"
                :options="startDateOptions"
                :placeholder="t('purchaseContract.selectStartDatePlaceholder')"
                @update:model-value="updateStartAndEndDate"
            >
                <template #value="{ value, placeholder }">
                    <div v-if="value !== null">
                        {{ value.text }}
                    </div>
                    <span v-else>
                        {{ placeholder }}
                    </span>
                </template>
                <template #option="{ option }">
                    {{ option.text }}
                </template>
            </dropdown>
        </div>
        <div class="whitespace-nowrap">
            <label
                for="contractEndDateDisplay"
                class="block font-semibold"
                >{{ t('endDate') }}</label
            >
            <span v-if="formattedEndDate === null">&mdash;</span>
            <span v-else>{{ formattedEndDate }}</span>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PlanSessionJoinDatesPickerFragment = graphql(/** GraphQL */ `
    fragment PlanSessionJoinDatesPickerFragment on Plan {
        contractDateOptions(input: $input) {
            startDate
            endDate
        }
    }
`)
</script>

<script setup lang="ts">
import Dropdown from 'primevue/dropdown'
import type { PropType } from 'vue'
import { computed, onMounted } from 'vue'
import {
    formatTimelessDate,
    isEndOfTime,
    parseLocal,
    toLocalIsoString
} from '~/composables/support/localization'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { useI18n } from 'vue-i18n'

const props = defineProps({
    plan: {
        type: Object as PropType<
            FragmentType<typeof PlanSessionJoinDatesPickerFragment>
        >,
        required: true
    },
    contractStartDate: {
        type: String as PropType<string | null>,
        default: null
    },
    contractEndDate: {
        type: String as PropType<string | null>,
        default: null
    }
})

const { t } = useI18n({ useScope: 'global' })
const planData = getFragmentData(PlanSessionJoinDatesPickerFragment, props.plan)

const emit = defineEmits({
    'update:contractStartDate': payload => {
        return typeof payload === 'string' || payload === null
    },
    'update:contractEndDate': payload => {
        return typeof payload === 'string' || payload === null
    }
})

const formattedStartDate = computed(() => {
    if (props.contractStartDate === null) {
        return ''
    }

    return formatTimelessDate(parseLocal(props.contractStartDate))
})

const formattedEndDate = computed(() => {
    if (props.contractEndDate === null) {
        return ''
    }

    const endDate = parseLocal(props.contractEndDate)
    if (isEndOfTime(endDate)) {
        return null
    }

    return formatTimelessDate(endDate)
})

function updateStartAndEndDate(startDateOption: {
    key: string
    endDate: Date
}) {
    emit('update:contractStartDate', startDateOption.key)
    emit('update:contractEndDate', toLocalIsoString(startDateOption.endDate))
}

const selectedStartDate = computed(() => {
    if (props.contractStartDate === null) {
        return null
    }

    const selectedStartDate = startDateOptions.value.find(
        option => option.key === props.contractStartDate
    )
    if (selectedStartDate === undefined) {
        return null
    }
    return selectedStartDate
})

const startDateOptions = computed(() => {
    return planData.contractDateOptions.map(option => {
        const startDate = parseLocal(option.startDate)
        const endDate = parseLocal(option.endDate)
        return {
            key: toLocalIsoString(startDate),
            text: formatTimelessDate(startDate),
            startDate: startDate,
            endDate: endDate
        }
    })
})

onMounted(() => {
    if (props.contractStartDate === null) {
        emit(
            'update:contractStartDate',
            toLocalIsoString(startDateOptions.value[0].startDate)
        )
        emit(
            'update:contractEndDate',
            toLocalIsoString(startDateOptions.value[0].endDate)
        )
    }
})
</script>
